import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Form, Button, Spinner, Badge, Accordion } from 'react-bootstrap';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface Source {
  docid?: string;
  eo_number?: string;
  title?: string;
  doc_type?: string;
  authority?: string;
  section_path?: string;
  section_type?: string;
  similarity?: number;
  source?: 'actual' | 'crm';
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  timestamp: number;
  model?: string;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const SUGGESTIONS = [
  'Какие способы закупок по 44-ФЗ?',
  'Сроки подачи заявки по 44-ФЗ',
  'Когда можно закупать у единственного поставщика по 223-ФЗ?',
  'Статья 1 44-ФЗ — сфера применения',
  'Какие права есть у работника при увольнении?',
];

const CodeBlock = ({ node, inline, className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || '');
  const code = String(children).replace(/\n$/, '');
  
  if (!inline && match) {
    return (
      <div className="position-relative my-3">
        <div className="d-flex justify-content-between align-items-center px-3 py-1 bg-dark text-light rounded-top" style={{ fontSize: '0.75rem' }}>
          <span>{match[1]}</span>
          <Button
            variant="link"
            size="sm"
            className="text-light p-0 text-decoration-none"
            onClick={() => navigator.clipboard.writeText(code)}
          >
            <i className="bi bi-copy me-1"></i>Копировать
          </Button>
        </div>
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  }
  
  return (
    <code className="bg-light px-1 py-0 rounded text-danger" style={{ fontSize: '0.875em' }} {...props}>
      {children}
    </code>
  );
};

export const RagPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = localStorage.getItem('rag_history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSourceMsg, setActiveSourceMsg] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    localStorage.setItem('rag_history', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim(),
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/api/rag/ask`, {
        question: userMsg.content,
        filters: {}
      }, { timeout: 300000 });

      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: res.data.answer || 'Нет ответа',
        sources: res.data.sources || [],
        timestamp: Date.now(),
        model: res.data.model,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Ошибка соединения с сервером');
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [loading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = () => {
    if (window.confirm('Очистить историю диалога?')) {
      setMessages([]);
      localStorage.removeItem('rag_history');
    }
  };

  const copyMessage = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const adjustTextareaHeight = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }
  };

  return (
    <div className="d-flex flex-column" style={{ height: 'calc(100vh - 56px)', background: '#f5f5f5' }}>
      {/* Header */}
      <div className="bg-white border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="bg-primary bg-gradient rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
            <i className="bi bi-robot text-white"></i>
          </div>
          <div>
            <h5 className="mb-0 fw-semibold">Юридический ассистент</h5>
            <small className="text-muted">Поиск по актуальному праву и документам</small>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline-danger" size="sm" onClick={clearChat}>
            <i className="bi bi-trash3 me-1"></i>
            Очистить
          </Button>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-grow-1 overflow-auto">
        <div className="py-4" style={{ maxWidth: 850, margin: '0 auto' }}>
          {messages.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 80, height: 80 }}>
                  <i className="bi bi-shield-check text-primary" style={{ fontSize: 40 }}></i>
                </div>
                <h4 className="text-dark mb-2">Чем могу помочь?</h4>
                <p className="text-muted mb-4">Задайте вопрос о законодательстве — AI найдёт релевантные документы</p>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-center px-3">
                {SUGGESTIONS.map(q => (
                  <Button
                    key={q}
                    variant="outline-secondary"
                    className="rounded-pill"
                    onClick={() => sendMessage(q)}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={msg.id}
                className={`px-4 py-3 ${msg.role === 'assistant' ? 'bg-white border-bottom' : ''}`}
                style={{ animation: 'fadeIn 0.3s ease-in' }}
              >
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                  {/* Avatar + Name */}
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {msg.role === 'user' ? (
                      <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>
                        <i className="bi bi-person text-white" style={{ fontSize: 14 }}></i>
                      </div>
                    ) : (
                      <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>
                        <i className="bi bi-robot text-white" style={{ fontSize: 14 }}></i>
                      </div>
                    )}
                    <span className="fw-semibold small">
                      {msg.role === 'user' ? 'Вы' : 'AI Ассистент'}
                    </span>
                    {msg.model && (
                      <Badge bg="light" text="dark" className="border">
                        <small>{msg.model}</small>
                      </Badge>
                    )}
                    <small className="text-muted ms-auto">{formatTime(msg.timestamp)}</small>
                  </div>

                  {/* Content */}
                  <div className="ms-5">
                    {msg.role === 'assistant' ? (
                      <div className="markdown-body">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{ code: CodeBlock }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    )}

                    {/* Sources */}
                    {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                      <div className="mt-3">
                        <Accordion>
                          <Accordion.Item eventKey={msg.id} className="border-0">
                            <Accordion.Header className="py-1">
                              <small className="text-muted">
                                <i className="bi bi-journal-text me-1"></i>
                                Источники ({msg.sources.length})
                              </small>
                            </Accordion.Header>
                            <Accordion.Body className="px-0 pt-2 pb-0">
                              {msg.sources.map((s, i) => (
                                <div key={i} className="d-flex align-items-start gap-2 mb-2 p-2 rounded bg-light">
                                  <div className="flex-grow-1">
                                    <div className="d-flex gap-2 mb-1 flex-wrap">
                                      <Badge bg={s.source === 'actual' ? 'success' : 'primary'} style={{ fontSize: '0.7rem' }}>
                                        {s.source === 'actual' ? 'Актуальное право' : 'CRM'}
                                      </Badge>
                                      {s.eo_number && s.eo_number !== 'N/A' && (
                                        <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>{s.eo_number}</Badge>
                                      )}
                                      <Badge bg="info" style={{ fontSize: '0.7rem' }}>
                                        {((s.similarity || 0) * 100).toFixed(1)}%
                                      </Badge>
                                    </div>
                                    <div className="small fw-semibold">{s.title || 'Без названия'}</div>
                                    {s.section_path && (
                                      <div className="small text-muted">
                                        <i className="bi bi-folder me-1"></i>
                                        {s.section_path}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </Accordion.Body>
                          </Accordion.Item>
                        </Accordion>
                      </div>
                    )}

                    {/* Actions */}
                    {msg.role === 'assistant' && (
                      <div className="mt-2 d-flex gap-2">
                        <Button
                          variant="link"
                          size="sm"
                          className="text-muted p-0 text-decoration-none"
                          onClick={() => copyMessage(msg.content)}
                        >
                          <i className="bi bi-copy me-1"></i>
                          Копировать
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Loading */}
          {loading && (
            <div className="px-4 py-3 bg-white border-bottom">
              <div style={{ maxWidth: 800, margin: '0 auto' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>
                    <i className="bi bi-robot text-white" style={{ fontSize: 14 }}></i>
                  </div>
                  <span className="fw-semibold small">AI Ассистент</span>
                </div>
                <div className="ms-5 d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="grow" size="sm" variant="primary" />
                  <Spinner animation="grow" size="sm" variant="primary" style={{ animationDelay: '0.15s' }} />
                  <Spinner animation="grow" size="sm" variant="primary" style={{ animationDelay: '0.3s' }} />
                  <span className="ms-2 small">Анализирую документы...</span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="px-4 py-3">
              <div style={{ maxWidth: 800, margin: '0 auto' }}>
                <div className="alert alert-danger d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-top px-4 py-3">
        <div style={{ maxWidth: 850, margin: '0 auto' }}>
          <Form onSubmit={handleSubmit}>
            <div className="d-flex gap-2 align-items-end">
              <div className="flex-grow-1 position-relative">
                <Form.Control
                  as="textarea"
                  ref={textareaRef}
                  rows={1}
                  placeholder="Задайте вопрос о законодательстве..."
                  value={input}
                  onChange={e => {
                    setInput(e.target.value);
                    adjustTextareaHeight();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  disabled={loading}
                  className="rounded-3 border"
                  style={{ resize: 'none', paddingRight: 40, minHeight: 44 }}
                />
                {input && (
                  <Button
                    variant="link"
                    size="sm"
                    className="position-absolute text-muted p-0"
                    style={{ right: 10, bottom: 10 }}
                    onClick={() => setInput('')}
                  >
                    <i className="bi bi-x-circle"></i>
                  </Button>
                )}
              </div>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !input.trim()}
                className="rounded-3 px-4"
                style={{ height: 44 }}
              >
                <i className="bi bi-send-fill"></i>
              </Button>
            </div>
            <small className="text-muted mt-1 d-block">
              <kbd>Enter</kbd> — отправить, <kbd>Shift</kbd>+<kbd>Enter</kbd> — новая строка
            </small>
          </Form>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4 {
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        .markdown-body p { margin-bottom: 0.75rem; }
        .markdown-body ul, .markdown-body ol { padding-left: 1.5rem; margin-bottom: 0.75rem; }
        .markdown-body table { width: 100%; margin-bottom: 1rem; border-collapse: collapse; }
        .markdown-body th, .markdown-body td { border: 1px solid #dee2e6; padding: 0.5rem; }
        .markdown-body th { background: #f8f9fa; }
        .markdown-body blockquote { border-left: 4px solid #0d6efd; padding-left: 1rem; color: #6c757d; margin: 1rem 0; }
      `}</style>
    </div>
  );
};
